import React from "react";
import {
  Box,
  Container,
  SimpleGrid,
  Stack,
  Text,
  Heading,
  Button,
  Image,
  Flex,
} from "@chakra-ui/react";
import { FiArrowRight } from "react-icons/fi";

const advisors = [
  {
    name: "Ota Akhigbe",
    role: "Advisor",
    focus: "Partnerships, institutions & delivery",
    image: "/images/institutional/advisor-ota-akhigbe.jpg",
    bio:
      "Ota Akhigbe works at the intersection of capital, governance and delivery across African institutions. With senior leadership experience spanning financial services, public-private partnerships and digitally enabled health systems, she brings a decision-level perspective on building partnerships and institutions that endure.",
  },
  {
    name: "Dr. Francis Ohanyido",
    role: "Advisor",
    focus: "Public health, policy & health systems",
    image: "/images/institutional/advisor-francis-ohanyido.png",
    bio:
      "Dr. Francis Ohanyido is a public health physician and development strategist with more than 25 years of experience across health reform, health systems, maternal and child health, nutrition, primary healthcare and universal health coverage. His work spans research, training, advocacy and programme implementation across Africa.",
  },
  {
    name: "Prof. Juliet Iwelunmor",
    role: "Advisor",
    focus: "Dissemination & implementation science",
    image: "/images/institutional/advisor-juliet-iwelunmor.png",
    bio:
      "Prof. Juliet Iwelunmor is a Professor of Medicine and Associate Director for Global Health and Dissemination at WashU Medicine. Her work focuses on dissemination and implementation science, health equity, sustainability and community-engaged approaches for ensuring evidence-based interventions last.",
  },
  {
    name: "Prof. Ganiyu Oboh",
    role: "Advisor",
    focus: "Applied biochemistry & nutritional science",
    image: "/images/institutional/advisor-ganiyu-oboh.png",
    bio:
      "Prof. Ganiyu Oboh is Professor of Applied Biochemistry at the Federal University of Technology Akure, where he leads the Functional Food and Nutraceutical Laboratory Unit. His research spans nutritional biochemistry, plant biochemistry, food chemistry, functional foods and neuroprotection.",
  },
];

function SectionLabel({ children }) {
  return (
    <Text
      fontSize="xs"
      fontWeight="700"
      letterSpacing="0.14em"
      textTransform="uppercase"
      color="#087F73"
      mb={4}
    >
      {children}
    </Text>
  );
}

function AboutPage() {
  return (
    <Box>
      {/* HERO */}
      <Box
        position="relative"
        overflow="hidden"
        bg="#F3F5F1"
        py={{ base: 20, md: 28, lg: 36 }}
      >
        <Container
          maxW="1400px"
          px={{ base: 5, md: 8, lg: 12 }}
        >
          <Box maxW="900px">
            <SectionLabel>ABOUT SERENA SPACE</SectionLabel>

            <Heading
              as="h1"
              fontFamily="'Poppins', sans-serif"
              fontSize={{ base: "3xl", md: "5xl", lg: "6xl" }}
              lineHeight="1.05"
              letterSpacing="-0.045em"
              fontWeight="700"
              color="#17211F"
            >
              Building the infrastructure for what comes next in African
              healthcare.
            </Heading>

            <Text
              mt={7}
              maxW="760px"
              fontSize={{ base: "lg", md: "xl" }}
              lineHeight="1.7"
              color="#4A5753"
            >
              Serena Space is building the scientific, technological and
              translational infrastructure needed to advance preventive and
              precision healthcare in Africa.
            </Text>
          </Box>
        </Container>
      </Box>

      {/* WHY SERENA EXISTS */}
      <Box bg="white" py={{ base: 20, md: 28 }}>
        <Container
          maxW="1400px"
          px={{ base: 5, md: 8, lg: 12 }}
        >
          <SimpleGrid
            columns={{ base: 1, lg: 2 }}
            spacing={{ base: 12, lg: 24 }}
            alignItems="start"
          >
            <Box>
              <SectionLabel>WHY WE EXIST</SectionLabel>

              <Heading
                as="h2"
                fontFamily="'Poppins', sans-serif"
                fontSize={{ base: "2xl", md: "4xl" }}
                lineHeight="1.15"
                letterSpacing="-0.035em"
                fontWeight="600"
                maxW="600px"
              >
                Better health science does not automatically become better
                care.
              </Heading>
            </Box>

            <Stack spacing={6} maxW="680px">
              <Text fontSize={{ base: "md", md: "lg" }} lineHeight="1.8">
                Scientific advances can reveal what is possible long before
                healthcare systems are ready to use them effectively.
              </Text>

              <Text fontSize={{ base: "md", md: "lg" }} lineHeight="1.8">
                The pathway between scientific possibility and patient outcome
                has to be built, tested and measured.
              </Text>

              <Text
                fontSize={{ base: "lg", md: "xl" }}
                lineHeight="1.7"
                fontWeight="600"
                color="#087F73"
              >
                That is the space Serena is building for.
              </Text>
            </Stack>
          </SimpleGrid>
        </Container>
      </Box>

      {/* WHAT WE ARE BUILDING */}
      <Box bg="#F9FAF8" py={{ base: 20, md: 28 }}>
        <Container
          maxW="1400px"
          px={{ base: 5, md: 8, lg: 12 }}
        >
          <SectionLabel>WHAT WE ARE BUILDING</SectionLabel>

          <Heading
            as="h2"
            fontFamily="'Poppins', sans-serif"
            fontSize={{ base: "2xl", md: "4xl" }}
            lineHeight="1.15"
            letterSpacing="-0.035em"
            fontWeight="600"
            maxW="760px"
            mb={{ base: 12, md: 16 }}
          >
            Three connected engines for moving health evidence into practice.
          </Heading>

          <SimpleGrid columns={{ base: 1, md: 3 }} spacing={6}>
            <Box
              bg="white"
              p={{ base: 7, md: 9 }}
              borderTop="3px solid"
              borderColor="#087F73"
            >
              <Text
                fontSize="xs"
                fontWeight="700"
                letterSpacing="0.12em"
                color="#087F73"
                mb={5}
              >
                01 — SCIENCE
              </Text>

              <Heading
                as="h3"
                fontSize={{ base: "xl", md: "2xl" }}
                letterSpacing="-0.025em"
                mb={4}
              >
                Generate and translate evidence.
              </Heading>

              <Text color="#596560" lineHeight="1.75">
                Preventive and precision health research, African health data,
                biomarkers, genomics, longitudinal evidence, translational
                science and research capacity.
              </Text>
            </Box>

            <Box
              bg="white"
              p={{ base: 7, md: 9 }}
              borderTop="3px solid"
              borderColor="#D9A441"
            >
              <Text
                fontSize="xs"
                fontWeight="700"
                letterSpacing="0.12em"
                color="#9A721F"
                mb={5}
              >
                02 — TECHNOLOGY
              </Text>

              <Heading
                as="h3"
                fontSize={{ base: "xl", md: "2xl" }}
                letterSpacing="-0.025em"
                mb={4}
              >
                Turn health intelligence into usable tools.
              </Heading>

              <Text color="#596560" lineHeight="1.75">
                We build technologies that help people, families and healthcare
                environments make better use of health information.
              </Text>
            </Box>

            <Box
              bg="white"
              p={{ base: 7, md: 9 }}
              borderTop="3px solid"
              borderColor="#7256A3"
            >
              <Text
                fontSize="xs"
                fontWeight="700"
                letterSpacing="0.12em"
                color="#7256A3"
                mb={5}
              >
                03 — ACTIVATION
              </Text>

              <Heading
                as="h3"
                fontSize={{ base: "xl", md: "2xl" }}
                letterSpacing="-0.025em"
                mb={4}
              >
                Make innovation work in real-world pathways.
              </Heading>

              <Text color="#596560" lineHeight="1.75">
                We design, implement and measure the pathways through which
                health innovations can become working practice and measurable
                outcomes.
              </Text>
            </Box>
          </SimpleGrid>
        </Container>
      </Box>

      {/* HOW WE WORK */}
      <Box bg="#17211F" color="white" py={{ base: 20, md: 28 }}>
        <Container
          maxW="1400px"
          px={{ base: 5, md: 8, lg: 12 }}
        >
          <SectionLabel>HOW WE WORK</SectionLabel>

          <Heading
            as="h2"
            fontFamily="'Poppins', sans-serif"
            fontSize={{ base: "2xl", md: "4xl" }}
            lineHeight="1.15"
            letterSpacing="-0.035em"
            fontWeight="600"
            maxW="700px"
            mb={{ base: 12, md: 16 }}
          >
            From evidence to action — and back again.
          </Heading>

          <SimpleGrid columns={{ base: 1, sm: 2, lg: 6 }} spacing={0}>
            {[
              "Evidence",
              "Translation",
              "Technology",
              "Pathway",
              "Outcome",
              "New evidence",
            ].map((step, index) => (
              <Box
                key={step}
                py={6}
                pr={{ lg: 5 }}
                borderTop="1px solid"
                borderColor="whiteAlpha.300"
              >
                <Text
                  fontSize="xs"
                  fontWeight="700"
                  color="whiteAlpha.500"
                  mb={3}
                >
                  0{index + 1}
                </Text>

                <Text
                  fontSize={{ base: "lg", md: "xl" }}
                  fontWeight="600"
                  letterSpacing="-0.02em"
                >
                  {step}
                </Text>
              </Box>
            ))}
          </SimpleGrid>
        </Container>
      </Box>

      {/* ADVISORY BOARD */}
      <Box bg="white" py={{ base: 20, md: 28 }}>
        <Container
          maxW="1400px"
          px={{ base: 5, md: 8, lg: 12 }}
        >
          <Box maxW="760px" mb={{ base: 12, md: 16 }}>
            <SectionLabel>ADVISORY BOARD</SectionLabel>

            <Heading
              as="h2"
              fontFamily="'Poppins', sans-serif"
              fontSize={{ base: "2xl", md: "4xl" }}
              lineHeight="1.15"
              letterSpacing="-0.035em"
              fontWeight="600"
            >
              Expertise across the pathway.
            </Heading>

            <Text
              mt={5}
              fontSize={{ base: "md", md: "lg" }}
              lineHeight="1.75"
              color="#596560"
            >
              Serena’s advisors bring perspectives across science, public
              health, implementation, research capacity, partnerships and
              applied health science.
            </Text>
          </Box>

          <SimpleGrid
            columns={{ base: 1, md: 2, lg: 3 }}
            spacing={{ base: 10, md: 12 }}
          >
            {advisors.map((advisor) => (
              <Box key={advisor.name}>
                <Box
                  overflow="hidden"
                  bg="#EEF1ED"
                  aspectRatio={4 / 5}
                >
                  <Image
                    src={advisor.image}
                    alt={advisor.name}
                    w="100%"
                    h="100%"
                    objectFit="cover"
                  />
                </Box>

                <Box pt={6}>
                  <Text
                    fontSize="xs"
                    fontWeight="700"
                    letterSpacing="0.08em"
                    textTransform="uppercase"
                    color="#087F73"
                    mb={2}
                  >
                    {advisor.role}
                  </Text>

                  <Heading
                    as="h3"
                    fontSize="xl"
                    letterSpacing="-0.025em"
                    mb={2}
                  >
                    {advisor.name}
                  </Heading>

                  <Text
                    fontSize="sm"
                    fontWeight="600"
                    color="#4A5753"
                    mb={4}
                  >
                    {advisor.focus}
                  </Text>

                  <Text
                    fontSize="sm"
                    lineHeight="1.75"
                    color="#66716D"
                  >
                    {advisor.bio}
                  </Text>
                </Box>
              </Box>
            ))}
          </SimpleGrid>
        </Container>
      </Box>

      {/* LEADERSHIP */}
      <Box bg="#F3F5F1" py={{ base: 20, md: 28 }}>
        <Container
          maxW="1400px"
          px={{ base: 5, md: 8, lg: 12 }}
        >
          <SectionLabel>LEADERSHIP</SectionLabel>

          <SimpleGrid
            columns={{ base: 1, md: 2 }}
            spacing={{ base: 10, md: 20 }}
            alignItems="center"
          >
            <Box
              overflow="hidden"
              bg="#E6EAE5"
              maxW="520px"
              aspectRatio={4 / 5}
            >
              <Image
                src="/images/institutional/founder-shaban.png"
                alt="Shaban Haruna, Founder and CEO of Serena Space"
                w="100%"
                h="100%"
                objectFit="cover"
              />
            </Box>

            <Box maxW="650px">
              <Text
                fontSize="xs"
                fontWeight="700"
                letterSpacing="0.08em"
                textTransform="uppercase"
                color="#087F73"
                mb={3}
              >
                Founder & CEO
              </Text>

              <Heading
                as="h2"
                fontSize={{ base: "2xl", md: "4xl" }}
                letterSpacing="-0.035em"
                mb={5}
              >
                Shaban Haruna
              </Heading>

              <Text
                fontSize={{ base: "md", md: "lg" }}
                lineHeight="1.8"
                color="#596560"
              >
                Serena Space is being built around a simple premise: Africa
                needs more than access to health innovation. It needs the
                scientific, technological and translational infrastructure
                required to turn that innovation into something usable,
                measurable and durable.
              </Text>
            </Box>
          </SimpleGrid>
        </Container>
      </Box>

      {/* FINAL CTA */}
      <Box bg="white" py={{ base: 20, md: 28 }}>
        <Container
          maxW="1400px"
          px={{ base: 5, md: 8, lg: 12 }}
        >
          <Flex
            direction={{ base: "column", md: "row" }}
            justify="space-between"
            align={{ base: "flex-start", md: "center" }}
            gap={8}
            borderTop="1px solid"
            borderColor="blackAlpha.100"
            pt={{ base: 10, md: 14 }}
          >
            <Box maxW="700px">
              <SectionLabel>WORK WITH SERENA</SectionLabel>

              <Heading
                as="h2"
                fontSize={{ base: "2xl", md: "4xl" }}
                letterSpacing="-0.035em"
              >
                Help move evidence into action.
              </Heading>
            </Box>

            <Button
              as="a"
              href="/partner"
              size="lg"
              px={7}
              bg="#087F73"
              color="white"
              rightIcon={<FiArrowRight />}
              _hover={{
                bg: "#066B61",
                textDecoration: "none",
              }}
            >
              Partner with Serena
            </Button>
          </Flex>
        </Container>
      </Box>
    </Box>
  );
}

export default AboutPage;